import React, { useState, useEffect, useRef, useCallback } from "react";

import CounterHeader from "./CounterHeader";
import CounterBody from "./CounterBody";
import CounterFooter from "./CounterFooter";
import CounterModal from "./CounterModal";
import CounterRoundComplete from "./CounterRoundComplete";
import CounterList from "./CounterList";
import CounterForm from "./CounterForm";
import CounterSettingsModal from "./CounterSettingsModal";

import { formatTime } from "../../utils/formatTime";
import {
  STORAGE_KEY,
  getInitialCounterState,
  getDailyProgressHint,
  getCurrentRoundTarget,
  stampLastSelected,
} from "../../utils/counterState";

import {
  THEME_STORAGE_KEY,
  getValidThemeId,
} from "../../utils/counterTheme";

import {
  VIBRATION_STORAGE_KEY,
  parseVibrationEnabled,
  triggerTapVibration,
} from "../../utils/haptics";

import useTimer from "../../hooks/useTimer";
import useCounterForm from "../../hooks/useCounterForm";
import useCounterActions from "../../hooks/useCounterActions";
import useCounterProgress from "../../hooks/useCounterProgress";

import "../../../../src/main.scss";

const Counter = () => {
  // -------------------------
  // UI state
  // -------------------------
  const [showAllLists, setShowAllLists] = useState(false);
  const [showCreateCounter, setShowCreateCounter] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [counterThemeId, setCounterThemeId] = useState(() =>
    getValidThemeId(
      typeof localStorage !== "undefined"
        ? localStorage.getItem(THEME_STORAGE_KEY)
        : null
    )
  );

  const [vibrationEnabled, setVibrationEnabled] =
    useState(parseVibrationEnabled);

  // -------------------------
  // Timer
  // -------------------------
  const [isRunning, setIsRunning] = useState(false);

  // -------------------------
  // Counter state
  // -------------------------
  const initialStateRef = useRef(getInitialCounterState());

  const [counters, setCounters] = useState(
    initialStateRef.current.counters
  );
  const [activeCounterId, setActiveCounterId] = useState(
    initialStateRef.current.activeCounterId
  );

  const activeCounter =
    counters.find((c) => c.id === activeCounterId) || null;

  const [openCounterId, setOpenCounterId] = useState(null);

  // -------------------------
  // Custom hooks
  // -------------------------
  const {
    showRoundComplete,
    goalComplete,
    goalRoundKind,
    logHistory,
    setGoalComplete,
    handleIncrement,
    handleDecrement,
    handleRoundStop,
    handleRoundContinue,
  } = useCounterProgress({
    activeCounter,
    activeCounterId,
    isRunning,
    setIsRunning,
    setCounters,
  });

  const {
    activeTab,
    setActiveTab,
    newCounterName,
    setNewCounterName,
    newCounterLimit,
    setNewCounterLimit,
    newCounterDays,
    setNewCounterDays,
    editingCounterId,
    formError,
    setFormError,
    handleCreateCounterSubmit,
    handleEditActiveCounter,
    openCreateForm,
    closeCreateForm,
  } = useCounterForm({
    counters,
    setCounters,
    setActiveCounterId,
    setGoalComplete,
    setIsRunning,
    setShowCreateCounter,
  });

  const {
    resetType,
    showModal,
    undoPayload,
    handleDeleteCounterClick,
    handleCountResetClick,
    handleTimerResetClick,
    cancelReset,
    confirmReset,
    handleUndo,
  } = useCounterActions({
    counters,
    activeCounterId,
    setCounters,
    setActiveCounterId,
    setIsRunning,
  });

  // -------------------------
  // Timer logic
  // -------------------------
  const handleTimerTick = useCallback((counterId) => {
    setCounters((prev) =>
      prev.map((c) =>
        c.id === counterId
          ? { ...c, spentTime: c.spentTime + 1 }
          : c
      )
    );
  }, []);

  useTimer({
    isRunning,
    canRun: Boolean(activeCounter),
    activeCounterId,
    onTick: handleTimerTick,
  });

  // -------------------------
  // Effects
  // -------------------------
  useEffect(() => {
    const handleUnload = () => {
      if (activeCounter && !activeCounter.isDisabled) {
        logHistory(
          activeCounter.id,
          activeCounter.currentCount,
          activeCounter.spentTime
        );
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    return () =>
      window.removeEventListener("beforeunload", handleUnload);
  }, [activeCounter, logHistory]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ counters, activeCounterId })
      );
    } catch {}
  }, [counters, activeCounterId]);

  useEffect(() => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, counterThemeId);
    } catch {}
  }, [counterThemeId]);

  useEffect(() => {
    try {
      localStorage.setItem(
        VIBRATION_STORAGE_KEY,
        vibrationEnabled ? "1" : "0"
      );
    } catch {}
  }, [vibrationEnabled]);

  const onTapFeedback = useCallback(() => {
    triggerTapVibration(vibrationEnabled);
  }, [vibrationEnabled]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (showModal) return cancelReset();
      if (showCreateCounter) return closeCreateForm();
      if (showAllLists) return setShowAllLists(false);

      if (showSettings) {
        setShowSettings(false);
        setIsRunning(true);
        return;
      }

      if (showRoundComplete) handleRoundStop();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () =>
      window.removeEventListener("keydown", handleKeyDown);
  }, [
    showModal,
    showCreateCounter,
    showAllLists,
    showSettings,
    showRoundComplete,
    cancelReset,
    closeCreateForm,
    handleRoundStop,
  ]);

  // -------------------------
  // Handlers
  // -------------------------
  const handleSelectCounter = (id) => {
    setCounters((prev) => stampLastSelected(prev, id));
    setActiveCounterId(id);
    setShowAllLists(false);
    setIsRunning(true);
  };

  // -------------------------
  // UI Helpers
  // -------------------------
  const percent = (() => {
    if (!activeCounter) return 0;

    const perRound =
      activeCounter.type === "goal"
        ? Math.max(
            1,
            getCurrentRoundTarget(activeCounter) ||
              activeCounter.perDayCount ||
              1
          )
        : Math.max(
            1,
            activeCounter.perDayCount ||
              activeCounter.limit ||
              1
          );

    if (
      activeCounter.type === "goal" &&
      ((activeCounter.completedCount || 0) >=
        (activeCounter.limit || 0) ||
        activeCounter.isDisabled)
    ) {
      return 100;
    }

    return Math.min(
      100,
      Math.floor(
        (activeCounter.currentCount / perRound) * 100
      )
    );
  })();

  const dialRadiusPx = 135;
  const dialDotAngleDeg = (percent / 100) * 360;

  const dailyProgressHint = activeCounter
    ? getDailyProgressHint(activeCounter)
    : null;

  // -------------------------
  // Render
  // -------------------------
  return (
    <div
      className={`contain counterWrapper counterWrapper--theme-${counterThemeId}`}
    >
      <div
        className=" counterWrapper__tasbeehDecor"
        aria-hidden="true"
      />

      <header className="counterWrapper__tasbeehBanner">
        <p
          className="counterWrapper__bismillah"
          lang="ar"
          dir="rtl"
        >
          بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
        </p>

        <span
          className="counterWrapper__tasbeehStar"
          aria-hidden="true"
        />

        <p className="counterWrapper__tasbeehTagline">
          Tasbeeh · Dhikr · Remembrance
        </p>
      </header>

      <CounterHeader
        isRunning={isRunning}
        onToggleTimer={() => setIsRunning((prev) => !prev)}
        onResetTimer={handleTimerResetClick}
        spentTime={formatTime(activeCounter?.spentTime || 0)}
        onTapFeedback={onTapFeedback}
      />

      <CounterBody
        activeCounter={activeCounter}
        percent={percent}
        dialDotAngleDeg={dialDotAngleDeg}
        dialRadiusPx={dialRadiusPx}
        onIncrement={handleIncrement}
        onEditCounter={handleEditActiveCounter}
        dailyProgressHint={dailyProgressHint}
        onTapFeedback={onTapFeedback}
      />

      <CounterFooter
        activeCounter={activeCounter}
        onResetCount={() =>
          handleCountResetClick(activeCounter)
        }
        onDecrement={handleDecrement}
        onShowCreateCounter={openCreateForm}
        onShowLists={() => {
          setShowAllLists(true);
          setIsRunning(false);
        }}
        onOpenSettings={() => {
          setShowSettings(true);
          setIsRunning(false);
        }}
        onTapFeedback={onTapFeedback}
      />

      {showSettings && (
        <CounterSettingsModal
          onClose={() => {
            setShowSettings(false);
            setIsRunning(true);
          }}
          themeId={counterThemeId}
          onThemeChange={(id) =>
            setCounterThemeId(getValidThemeId(id))
          }
          vibrationEnabled={vibrationEnabled}
          onVibrationChange={(next) => {
            setVibrationEnabled(next);
            if (next) triggerTapVibration(true);
          }}
        />
      )}

      {showModal && (
        <CounterModal
          resetType={resetType}
          onCancel={cancelReset}
          onConfirm={confirmReset}
        />
      )}

      {showRoundComplete && activeCounter && (
        <CounterRoundComplete
          activeCounter={activeCounter}
          goalComplete={goalComplete}
          goalRoundKind={goalRoundKind}
          onStop={handleRoundStop}
          onContinue={handleRoundContinue}
        />
      )}

      {showAllLists && (
        <CounterList
          counters={counters}
          activeCounterId={activeCounterId}
          onSelectCounter={handleSelectCounter}
          onDeleteCounter={handleDeleteCounterClick}
          onEditCounter={handleEditActiveCounter}
          openCounterId={openCounterId}
          setOpenCounterId={setOpenCounterId}
          onCancel={() => {
            setShowAllLists(false);
            setIsRunning(true);
          }}
        />
      )}

      {showCreateCounter && (
        <CounterForm
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          newCounterName={newCounterName}
          setNewCounterName={setNewCounterName}
          newCounterLimit={newCounterLimit}
          setNewCounterLimit={setNewCounterLimit}
          newCounterDays={newCounterDays}
          setNewCounterDays={setNewCounterDays}
          editingCounterId={editingCounterId}
          formError={formError}
          clearFormError={() => setFormError("")}
          onSubmit={handleCreateCounterSubmit}
          onCancel={closeCreateForm}
        />
      )}

      {undoPayload && (
        <div className="undoToast">
          <p>{undoPayload.message}</p>

          {undoPayload.snapshot && (
            <button type="button" onClick={handleUndo}>
              Undo
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Counter;